import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Select from "@atlaskit/select";
import { getJiraProjects, getJiraIssueTypesForProject, getJiraProjectById } from "../../../../redux/actions/jiraActions";
import { isEqual } from "lodash";

const EMPTY_PROJECT_IDS = [];

const toProjectOption = (project) => {
  return { label: project.name + "[" + project.key + "]", value: project.id };
};

const toFallbackProjectOption = (projectId) => {
  return { label: `Project ${projectId}`, value: projectId };
};

const toProjectId = (projectId) => {
  return projectId === undefined || projectId === null ? "" : String(projectId);
};

const optionMatchesId = (option, projectId) => {
  return toProjectId(option?.value) === toProjectId(projectId);
};

const toSelectedOptions = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean);
  }
  return value ? [value] : [];
};

const mergeOptions = (options, selectedOptions) => {
  const uniqueOptions = new Map();
  [...selectedOptions, ...options].filter(Boolean).forEach((option) => {
    uniqueOptions.set(toProjectId(option.value), option);
  });
  const optionIds = new Set(options.map((option) => toProjectId(option.value)));
  return [
    ...options.map((option) => uniqueOptions.get(toProjectId(option.value))),
    ...selectedOptions.filter((option) => !optionIds.has(toProjectId(option.value))),
  ];
};

const ProjectSelectList = ({
  onSelectProject,
  selectedProjectId,
  onSelectProjects,
  selectedProjectIds = EMPTY_PROJECT_IDS,
  isMulti = false,
  loadIssueTypes = true,
  placeholder = "Select Jira Project",
  inputId = "jira-project",
  isDisabled = false,
}) => {
  const [jiraProjects, setJiraProjects] = useState([]);
  const [value, setValue] = useState(isMulti ? [] : null);
  const dispatch = useDispatch();
  const timeoutRef = useRef(null);
  const requestedProjectIdsRef = useRef(new Set());

  const projects = useSelector((state) => state.jira.projects);

  useEffect(() => {
    const options = projects.map(toProjectOption);
    const selectedOptions = toSelectedOptions(value);
    const selectedIds = isMulti
      ? (Array.isArray(selectedProjectIds) ? selectedProjectIds : []).map(toProjectId).filter(Boolean)
      : selectedProjectId
      ? [toProjectId(selectedProjectId)]
      : [];
    const fallbackOptions = selectedIds
      .filter(
        (projectId) =>
          !options.some((option) => optionMatchesId(option, projectId)) &&
          !selectedOptions.some((option) => optionMatchesId(option, projectId))
      )
      .map(toFallbackProjectOption);
    const availableOptions = mergeOptions(options, [...selectedOptions, ...fallbackOptions]);

    setJiraProjects((currentOptions) => {
      return isEqual(currentOptions, availableOptions) ? currentOptions : availableOptions;
    });

    if (isMulti) {
      const nextSelectedOptions = selectedIds
        .map((projectId) => availableOptions.find((option) => optionMatchesId(option, projectId)))
        .filter(Boolean);
      if (!isEqual(value, nextSelectedOptions)) {
        setValue(nextSelectedOptions);
      }
    } else {
      const selectedOption =
        selectedIds.length > 0 ? availableOptions.find((option) => optionMatchesId(option, selectedIds[0])) || null : null;
      if (!isEqual(value, selectedOption)) {
        setValue(selectedOption);
      }
    }

    selectedIds
      .filter((projectId) => !options.some((option) => optionMatchesId(option, projectId)))
      .forEach((projectId) => {
        if (!requestedProjectIdsRef.current.has(projectId)) {
          requestedProjectIdsRef.current.add(projectId);
          dispatch(getJiraProjectById(projectId));
        }
      });
  }, [dispatch, isMulti, projects, selectedProjectId, selectedProjectIds, value]);

  useEffect(() => {
    if (!isMulti && loadIssueTypes && selectedProjectId) {
      dispatch(getJiraIssueTypesForProject(selectedProjectId));
    }
  }, [dispatch, isMulti, loadIssueTypes, selectedProjectId]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onLookup = (inputValue, actionMeta) => {
    if (actionMeta?.action && actionMeta.action !== "input-change") {
      return inputValue;
    }
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(function () {
      dispatch(getJiraProjects(inputValue));
    }, 500);
    return inputValue;
  };
  const onSelect = (project) => {
    setValue(project);
    if (isMulti) {
      const projectIds = (project || []).map((selectedProject) => selectedProject.value);
      onSelectProjects?.(projectIds);
      return;
    }

    const projectId = project?.value;
    onSelectProject?.(projectId);
  };
  return (
    <Select
      inputId={inputId}
      className="ihub-jira-project-list"
      options={jiraProjects}
      value={value}
      placeholder={placeholder}
      onChange={onSelect}
      onInputChange={onLookup}
      isCompact={true}
      isMulti={isMulti}
      isDisabled={isDisabled}
    />
  );
};
export default ProjectSelectList;
